// URL scheme + email canonicalization helpers shared across endpoints.

export function lowerEmail(s: unknown): string {
  return String(s ?? '').trim().toLowerCase();
}

// Throws on anything that isn't http: or https:. Used at block-render time
// before persisting links and again before issuing redirects, so an attacker
// who somehow injects a 'javascript:' URL into broadcast_links still can't
// trigger the click handler.
export function assertSafeUrl(url: string): void {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new Error('invalid_url');
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    throw new Error(`unsafe_url_scheme:${parsed.protocol}`);
  }
}

export function isSafeUrl(url: string): boolean {
  try {
    assertSafeUrl(url);
    return true;
  } catch {
    return false;
  }
}

export function isMailto(url: string): boolean {
  try {
    return new URL(url).protocol === 'mailto:';
  } catch {
    return false;
  }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function isLikelyEmail(s: string): boolean {
  return EMAIL_RE.test(s);
}

// Schemes safe to embed in email links/anchors. tel: and mailto: stay
// because they're legitimate in newsletters; everything else is rejected
// to block javascript:, data:, vbscript:, file:, custom schemes.
export function safeEmailHref(href: string): string {
  try {
    const u = new URL(href);
    const ok = u.protocol === 'http:' || u.protocol === 'https:'
      || u.protocol === 'mailto:' || u.protocol === 'tel:';
    return ok ? href : '';
  } catch {
    return '';
  }
}

// Image src must be fetchable over HTTP. data: would inline arbitrary
// content (potentially malicious SVGs); we don't allow it.
export function safeImageSrc(src: string): string {
  try {
    const u = new URL(src);
    return u.protocol === 'http:' || u.protocol === 'https:' ? src : '';
  } catch {
    return '';
  }
}
